import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { 
  Wrench, X, Crown, Home, Building2, ChevronUp, ChevronDown,
  User, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

// Pre-configured test accounts for quick switching (credentials from env)
const TEST_ACCOUNTS = [
  {
    id: 'founder',
    email: process.env.REACT_APP_DEV_ADMIN_EMAIL || 'founder@fbc.com',
    password: process.env.REACT_APP_DEV_ADMIN_PASS || '',
    name: 'FBC Founder',
    role: 'admin',
    icon: Crown,
    color: 'bg-amber-500',
    description: 'Full admin access'
  },
  {
    id: 'buyer',
    email: process.env.REACT_APP_DEV_BUYER_EMAIL || 'buyer@10bkc.com',
    password: process.env.REACT_APP_DEV_BUYER_PASS || '',
    name: 'TEN BKC Buyer',
    role: 'buyer',
    icon: Home,
    color: 'bg-blue-500',
    description: 'Buyer at TEN BKC project'
  },
  {
    id: 'developer',
    email: process.env.REACT_APP_DEV_DEV_EMAIL || 'developer@testcorp.com',
    password: process.env.REACT_APP_DEV_DEV_PASS || '',
    name: 'Test Developer',
    role: 'developer',
    icon: Building2,
    color: 'bg-emerald-500',
    description: 'Developer Console access'
  },
  {
    id: 'professional',
    email: process.env.REACT_APP_DEV_PROF_EMAIL || 'professional@fbc.com',
    password: process.env.REACT_APP_DEV_PROF_PASS || '',
    name: 'Test Professional',
    role: 'professional',
    icon: User,
    color: 'bg-purple-500',
    description: 'Professional Dashboard access'
  }
];

export default function DevToolsPanel() {
  const { user, login, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(() => {
    const saved = localStorage.getItem('fbc_dev_tools_visible');
    return saved !== 'false';
  });
  const [isMinimized, setIsMinimized] = useState(false);
  const [switching, setSwitching] = useState(null);

  // Framer-motion values for draggable button position (persisted to localStorage).
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const btnRef = useRef(null);

  // Restore saved position once on mount, clamped to current viewport.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('fbc_dev_tools_offset');
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (typeof saved?.x === 'number' && typeof saved?.y === 'number') {
        x.set(saved.x);
        y.set(saved.y);
        // After paint, clamp back into view in case viewport size changed.
        requestAnimationFrame(() => clampIntoView());
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the button inside the visible viewport; called after drag end and on restore.
  const clampIntoView = () => {
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const pad = 8;
    let nx = x.get();
    let ny = y.get();
    if (rect.left < pad) nx += (pad - rect.left);
    if (rect.top < pad) ny += (pad - rect.top);
    if (rect.right > window.innerWidth - pad) nx -= (rect.right - (window.innerWidth - pad));
    if (rect.bottom > window.innerHeight - pad) ny -= (rect.bottom - (window.innerHeight - pad));
    x.set(nx);
    y.set(ny);
    try {
      localStorage.setItem('fbc_dev_tools_offset', JSON.stringify({ x: nx, y: ny }));
    } catch { /* noop */ }
  };

  // Toggle visibility persistence
  useEffect(() => {
    localStorage.setItem('fbc_dev_tools_visible', isVisible.toString());
  }, [isVisible]);

  const handleQuickSwitch = async (account) => {
    if (switching) return;
    
    setSwitching(account.id);
    
    try {
      // Use the login function from AuthContext directly
      // This handles token storage and state properly
      await login(account.email, account.password);
      
      toast.success(`Switched to ${account.name}`, {
        description: `Role: ${account.role.toUpperCase()}`
      });
    } catch (err) {
      console.error('Quick switch error:', err);
      toast.error('Switch failed', { description: err.message || 'Login failed' });
    } finally {
      setSwitching(null);
    }
  };

  const getCurrentAccountInfo = () => {
    if (!user) return null;
    return TEST_ACCOUNTS.find(a => a.email === user.email) || {
      id: 'custom',
      name: user.name,
      role: user.role,
      icon: User,
      color: 'bg-gray-500',
      description: 'Custom account'
    };
  };

  const currentAccount = getCurrentAccountInfo();

  // Don't render if dev tools are hidden
  if (!isVisible) {
    return (
      <button
        onClick={() => setIsVisible(true)}
        className="fixed bottom-4 right-4 z-[9999] p-2 bg-gray-800 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
        title="Show Dev Tools"
      >
        <Wrench className="h-4 w-4" />
      </button>
    );
  }

  return (
    <motion.div
      className="fixed bottom-20 right-4 z-[9999]"
    >
      <AnimatePresence>
        {isOpen && !isMinimized && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="mb-2"
          >
            <Card className="w-72 shadow-2xl border-2 border-amber-500/30 bg-background/95 backdrop-blur-sm">
              {/* Header */}
              <div className="p-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-amber-600" />
                  <span className="font-semibold text-sm">Dev Tools</span>
                  <Badge variant="outline" className="text-[10px] border-amber-500/50 text-amber-600">
                    DEV MODE
                  </Badge>
                </div>
                <div className="flex items-center gap-1">
                  <button 
                    onClick={() => setIsMinimized(true)}
                    className="p-1 hover:bg-muted rounded"
                    title="Minimize"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => setIsVisible(false)}
                    className="p-1 hover:bg-muted rounded"
                    title="Hide Dev Tools"
                  >
                    <EyeOff className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              {/* Current User */}
              <div className="p-3 border-b bg-muted/30">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">
                  Currently Logged In As
                </p>
                {currentAccount ? (
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full ${currentAccount.color} flex items-center justify-center`}>
                      <currentAccount.icon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{currentAccount.name}</p>
                      <p className="text-[10px] text-muted-foreground">{currentAccount.role.toUpperCase()}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground italic">Not logged in</p>
                )}
              </div>
              
              {/* Quick Switch Accounts */}
              <div className="p-3">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">
                  Quick Switch Account
                </p>
                <div className="space-y-2">
                  {TEST_ACCOUNTS.map((account) => {
                    const Icon = account.icon;
                    const isActive = user?.email === account.email;
                    const isLoading = switching === account.id;
                    
                    return (
                      <button
                        key={account.id}
                        onClick={() => handleQuickSwitch(account)}
                        disabled={isLoading || isActive}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all text-left
                          ${isActive 
                            ? 'bg-primary/10 border-2 border-primary/30 cursor-default' 
                            : 'hover:bg-muted border-2 border-transparent'
                          }
                          ${isLoading ? 'opacity-70' : ''}
                        `}
                      >
                        <div className={`w-8 h-8 rounded-full ${account.color} flex items-center justify-center shrink-0`}>
                          {isLoading ? (
                            <RefreshCw className="h-4 w-4 text-white animate-spin" />
                          ) : (
                            <Icon className="h-4 w-4 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{account.name}</p>
                          <p className="text-[10px] text-muted-foreground">{account.description}</p>
                        </div>
                        {isActive && (
                          <Badge className="bg-primary/20 text-primary text-[10px]">Active</Badge>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              {/* Footer */}
              <div className="px-3 pb-3">
                <Separator className="mb-3" />
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>Show in production?</span>
                  <Switch 
                    checked={false} 
                    disabled 
                    className="scale-75"
                  />
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Draggable wrapper (motion.div) — inner button is a normal DOM button so clicks aren't swallowed by framer's drag gesture */}
      <motion.div
        ref={btnRef}
        drag
        dragMomentum={false}
        dragElastic={0}
        style={{ x, y, touchAction: 'none' }}
        onDragEnd={() => { clampIntoView(); }}
        className="inline-block"
        title="Drag to move • Click to open"
      >
        <button
          type="button"
          onClick={() => {
            if (isMinimized) {
              setIsMinimized(false);
            } else {
              setIsOpen(!isOpen);
            }
          }}
          className={`
            flex items-center gap-2 px-4 py-2 rounded-full shadow-lg transition-all cursor-grab active:cursor-grabbing hover:scale-105 active:scale-95
            ${isOpen ? 'bg-amber-500 text-white' : 'bg-gray-800 text-white hover:bg-gray-700'}
          `}
        >
        {isMinimized ? (
          <>
            <ChevronUp className="h-4 w-4" />
            <span className="text-sm font-medium">Dev Tools</span>
            {currentAccount && (
              <Badge variant="secondary" className="text-[10px]">
                {currentAccount.role}
              </Badge>
            )}
          </>
        ) : (
          <>
            <Wrench className="h-4 w-4" />
            <span className="text-sm font-medium">
              {isOpen ? 'Close' : 'Dev Tools'}
            </span>
            {!isOpen && currentAccount && (
              <div className={`w-5 h-5 rounded-full ${currentAccount.color} flex items-center justify-center`}>
                <currentAccount.icon className="h-3 w-3 text-white" />
              </div>
            )}
          </>
        )}
        </button>
      </motion.div>
    </motion.div>
  );
}
